<template>
  <div class="w-full flex items-center justify-between">
    <div class="w-full">
      <div class=" text-sm flex items-center">
        {{ $t('components.campaign.elements.customer.customers', { count: _campaign.customerIDs ? _campaign.customerIDs.length : '0' }, _campaign.customerIDs ? _campaign.customerIDs.length : 0) }}
      </div>
    </div>

    <div class="flex items-center space-x-2 flex-shrink-0">
      <o-button icon="customer" size="sm" flat @click="handleOpenSelectCustomers">
        {{ $t('components.campaign.elements.customer.title') }}
      </o-button>
    </div>

    <o-modal
      ref="modal"
      :title="$t('components.campaign.elements.customer.title')"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        { text: $t('general.apply'), type: 'confirm', variant: 'success', callback: handleConfirm },
      ]"
      modal-class="m-2 bg-gray-50 dark:bg-gray-900 dark:border rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-3xl sm:w-full h-full max-h-lg"
      content-class="mx-auto max-w-3xl h-full flex flex-col"
    >
      <template #header>
        <div class="w-full p-2 flex items-center justify-between">
          <div>
            <div v-if="loading" class="flex items-center">
              <o-loader
                :scale="0.4"
                class="h-8 w-8 mr-4"
              />
              <span class="align-middle">{{ $t('customer.loading') }} {{ syncProgress ? `(${Math.round(syncProgress * 100)}%)` : '' }} ...</span>
            </div>
            <h4 v-else class="font-semibold text-md leading-none">
              <span>{{ $t('customer.customersSelected', { count: customerIDs.length }, customerIDs.length) }}</span>
            </h4>
          </div>

          <div class="flex items-center">
            <label class="text-sm  mr-2">{{ $t('components.campaign.elements.customer.show') }}</label>
            <toggle-field v-model="onlySelected" />
          </div>
        </div>
      </template>

      <div class="text-left h-full flex items-start space-x-4 flex-shrink-0">
        <div class="w-1/3 h-full z-80">
          <o-scroll class="h-full">
            <div class="">
              <template v-for="(category, index) in filters.categorySets.marketing" :key="index">
                <o-collapse
                  v-if="!category.features || $features.all(category.features)"
                  header-class="flex flex-row items-center h-10 justify-between px-2  text-sm font-medium uppercase"
                  body-class="border-t border-gray-200 dark:border-gray-800"
                  class="rounded-md bg-white border dark:bg-gray-800/50 dark:border-gray-800 mb-2"
                >
                  <template #header>
                    <div class="flex justify-between w-full items-center">
                      {{ category.label }}
                      <o-chip
                        v-if="filters.categoryActiveCount(category)"
                        size="sm"
                        variant="info"
                        flat
                        square
                        class="mr-3"
                      >
                        {{ filters.categoryActiveCount(category) }}
                      </o-chip>
                    </div>
                  </template>
                  <div class="px-2 py-4 space-y-6">
                    <template v-for="filterKey in category.filters" :key="filterKey">
                      <o-field-group
                        v-if="filters.filterStates[filterKey]"
                        :name="filters.filterStates[filterKey].label"
                        :label="filters.filterStates[filterKey].label"
                        label-class="block  font-medium float-left text-sm"
                        :info="filters.filterStates[filterKey].info"
                        :status="filters.getValidation(filterKey)?.status ?? null"
                        :message="filters.getValidation(filterKey)?.message ?? null"
                        class="block mb-4"
                      >
                        <template #control>
                          <div>
                            <o-chip
                              v-if="!filters.isFilterEmpty(filterKey)"
                              class="float-right"
                              icon="x"
                              icon-after
                              size="sm"
                              variant="info"
                              flat
                              square
                              @action="filters.clearFilter(filterKey)"
                            >
                              <o-loader v-if="filters.filterStates[filterKey].isLoading" :scale="0.4" class="h-3 w-3" />
                              <template v-else>
                                {{ filters.filterStates[filterKey].items?.length ?? 0 }}
                              </template>
                            </o-chip>
                          </div>
                        </template>

                        <list-picker
                          v-if="filters.filterStates[filterKey].kind === 'list'"
                          v-model="filters.filterStates[filterKey].value"
                          :options="filters.filterStates[filterKey].options ?? []"
                          :option-count="filters.filterStates[filterKey].optionCount"
                        />

                        <toggle-field
                          v-else-if="filters.filterStates[filterKey].kind === 'boolean'"
                          v-model="filters.filterStates[filterKey].value"
                        />

                        <component
                          :is="resolveFilterComponent(filters.filterStates[filterKey].component)"
                          v-else-if="filters.filterStates[filterKey].component"
                          v-model="filters.filterStates[filterKey].value"
                          v-bind="filters.filterStates[filterKey].props"
                          :disabled="filters.filterStates[filterKey].disabled"
                          :options="filters.filterStates[filterKey].options"
                          :items="filters.filterStates[filterKey].options"
                          :custom="filters.filterStates[filterKey].custom"
                          :name="filters.filterStates[filterKey].label"
                          :status="filters.getValidation(filterKey)?.status ?? null"
                        />
                      </o-field-group>
                    </template>
                  </div>
                </o-collapse>
              </template>
            </div>
          </o-scroll>
        </div>

        <div class="w-2/3 h-full flex flex-col">
          <text-field
            v-model="filters.filterStates.search.value"
            :placeholder="$t('customer.search')"
            leading="search"
            class="mb-3"
            clear
          />

          <div class="h-full bg-white dark:bg-gray-900 rounded-lg border overflow-hidden flex flex-col">
            <div class="flex items-center justify-between px-3 py-2 border-b">
              <p class="text-sm opacity-80 font-medium leading-none">
                {{ $t('general.customer') }}
              </p>

              <div class="flex items-center space-x-2">
                <label class="text-sm opacity-80">{{ $t('general.selectAll') }}</label>
                <input
                  ref="select-all"
                  type="checkbox"
                  :checked="hasSelectedAllValidResults"
                  class="o-checkbox appearance-none h-4 w-4 border border-gray-300 rounded  checked:bg-blue-600 checked:border-blue-600"
                  @click="handleSelectAll"
                >
              </div>
            </div>
            <o-table
              :data="filtered"
              :limit="20"
              :filter-keys="[
                'firstName',
                'lastName'
              ]"
              scroll
              container-class="overflow-hidden"
              class="h-full"
            >
              <template #row="{ row }">
                <template v-if="isSelectableCustomer(row)">
                  <label
                    :class="[
                      'py-1 px-3 flex flex-row-reverse justify-between items-center rounded transition duration-200',
                      isCustomerSelected(row.idCustomer) ? 'bg-primary-100/50 dark:bg-primary-800/20' : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                    ]"
                  >
                    <input
                      type="checkbox"
                      :checked="isCustomerSelected(row.idCustomer)"
                      class="o-checkbox appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded checked:bg-primary-600 checked:border-primary-600 flex-shrink-0"
                      @change="handleToggleCustomer(row.idCustomer, $event)"
                    >
                    <div class="flex items-center justify-between w-full">
                      <div class="flex items-center space-x-2">
                        <o-avatar :items="[row]" />

                        <div class="text-sm">
                          <p>
                            {{ row.displayName }}
                          </p>
                          <p class="opacity-80">
                            {{ row.email }} <span v-if="row.email && row.phone">-</span> {{ row.phone }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                </template>

                <template v-else>
                  <div class="py-1 px-3 flex flex-row-reverse justify-between items-center rounded transition duration-200">
                    <div class="flex items-center justify-between w-full">
                      <div class="flex items-center space-x-2">
                        <o-avatar :items="[row]" class="opacity-75" />

                        <div class="text-sm">
                          <p class="opacity-60">
                            {{ row.displayName }}
                          </p>
                          <p class="opacity-60">
                            {{ row.email }} <span v-if="row.email && row.phone">-</span> {{ row.phone }}
                          </p>
                        </div>
                      </div>

                      <div class="flex items-center space-x-2">
                        <o-chip v-if="isCampaignTypeEmail && row.emailBounced" variant="warning" size="sm" flat>
                          {{ $t('components.campaign.elements.customer.recentBounces') }}
                        </o-chip>

                        <o-chip v-if="isCampaignTypeEmail && !row.email" variant="warning" size="sm" flat>
                          {{ $t('components.campaign.elements.customer.noEmail') }}
                        </o-chip>

                        <o-chip v-if="isCampaignTypeSMS && !row.phone" variant="warning" size="sm" flat>
                          {{ $t('components.campaign.elements.customer.noPhone') }}
                        </o-chip>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </o-table>
          </div>
        </div>
      </div>
    </o-modal>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import FieldsWithDateRange from '@/components/customer/list/FieldsWithDateRange.vue'
import DateOfBirthFilter from '@/components/customer/list/DateOfBirthFilter.vue'
import EmployeesFilter from '@/components/customer/list/EmployeesFilter.vue'
import ProductsFilter from '@/components/customer/list/ProductsFilter.vue'
import ServicesFilter from '@/components/customer/list/ServicesFilter.vue'
import DateRangeFilter from '@/components/filters/elements/DateRange.vue'
import ListPicker from '@/components/elements/facetedFilter/ListPicker.vue'

import {
  OAvatar,
  OTable,
  OCustomField,
} from '@/components/elements'

import {
  CheckboxField,
  CheckboxGroupField,
  DateRangeOptionalField,
  RadioGroupField,
  SegmentField,
  SelectField,
  TextField,
  ToggleField,
} from '@/components/fields'

import { useCustomerFilters } from '~/composables/filters/useCustomerFilters'

import { useCustomerStore } from '~/stores/useCustomerStore'
import { useFormStore } from '~/stores/useFormStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { useServiceStore } from '~/stores/useServiceStore'
import { useSegmentStore } from '~/stores/useSegmentStore'
import { useProductStore } from '~/stores/useProductStore'

const customerStore = useCustomerStore()
const formStore = useFormStore()
const employeeStore = useEmployeeStore()
const serviceStore = useServiceStore()
const segmentStore = useSegmentStore()
const productStore = useProductStore()

interface Props {
  campaign: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:campaign', value: any): void
}>()

const modal = ref<any>()
const customerIDs = ref<any[]>([])
const filteredResults = ref<any[]>([])
const filters = reactive(useCustomerFilters())
const isFiltering = ref(false)
const onlySelected = ref(false)

const isSyncing = computed(() => customerStore.isSyncing)
const syncProgress = computed(() => customerStore.syncProgress)
const syncDate = computed(() => customerStore.syncDate)
const all = computed(() => customerStore.all)
const forms = computed(() => formStore.all)
const employees = computed(() => employeeStore.all)
const services = computed(() => serviceStore.categorised)
const segments = computed(() => segmentStore.all)
const products = computed(() => productStore.all)

const _campaign = computed({
  get () {
    return props.campaign
  },
  set (value: any) {
    emit('update:campaign', value)
  },
})

const filterComponents: Record<string, any> = {
  CheckboxField,
  CheckboxGroupField,
  DateOfBirthFilter,
  DateRangeFilter,
  DateRangeOptionalField,
  EmployeesFilter,
  FieldsWithDateRange,
  OCustomField,
  ProductsFilter,
  RadioGroupField,
  SegmentField,
  SelectField,
  ServicesFilter,
  TextField,
  ToggleField,
}

const resolveFilterComponent = (componentName?: string) => {
  if (!componentName) return null
  return filterComponents[componentName] || componentName
}

const filtered = computed(() => {
  let items: any[]

  if (filters.isEmpty) {
    items = all.value
  } else {
    items = filteredResults.value
  }

  if (onlySelected.value) {
    return items
      .filter((item: any) => {
        return isCustomerSelected(item?.idCustomer)
      })
  }

  return items
})

const filterValues = computed(() => {
  const values: Record<string, any> = {}
  for (const [key, state] of Object.entries(filters.filterStates)) {
    values[key] = (state as any).value
  }
  return values
})

const filterCount = computed(() => {
  const filtersData = _campaign.value.filters
  return filtersData ? Object.keys(filtersData).length : 0
})

const filteredTotal = computed(() => {
  return filtered.value.length
})

const isCampaignTypeEmail = computed(() => {
  return _campaign.value.isCampaignTypeEmail
})

const isCampaignTypeSMS = computed(() => {
  return _campaign.value.isCampaignTypeSMS
})

const isMarketingCampaign = computed(() => {
  return _campaign.value.isContentTypeMarketing || _campaign.value.contentType === 0
})

const normalisedNumber = (value: any): number | null => {
  if (value && typeof value === 'object' && 'value' in value) {
    return normalisedNumber(value.value)
  }

  if (value == null || value === '') return null
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value)
  }

  return typeof value === 'number' ? value : null
}

const hasValidEmail = (email: any) => {
  return typeof email === 'string'
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const isSelectableCustomer = (customer: any) => {
  const deleted = normalisedNumber(customer.deleted)
  if (deleted != null && deleted !== 0) {
    return false
  }

  if (isCampaignTypeEmail.value) {
    if (!hasValidEmail(customer.email) || customer.emailBounced) {
      return false
    }

    return !isMarketingCampaign.value || normalisedNumber(customer.massEmailUnsubscribed) !== 1
  }

  if (isCampaignTypeSMS.value) {
    if (!customer.phone) {
      return false
    }

    return !isMarketingCampaign.value || normalisedNumber(customer.massSMSUnsubscribed) !== 1
  }

  return true
}

const customerIdKey = (id: any) => String(id)

const isCustomerSelected = (id: any) => {
  const key = customerIdKey(id)
  return customerIDs.value
    .some((customerID: any) => customerIdKey(customerID) === key)
}

const customerByID = computed(() => {
  return new Map(all.value.map((customer: any) => [customerIdKey(customer.idCustomer), customer]))
})

const validResults = computed(() => {
  return filtered.value
    .filter(isSelectableCustomer)
})

const selectedValidResultIDs = computed(() => {
  return validResults.value
    .map((customer: any) => customer.idCustomer)
})

const hasSelectedAllValidResults = computed(() => {
  return selectedValidResultIDs.value.length > 0
    && selectedValidResultIDs.value.every(isCustomerSelected)
})

const loading = computed(() => {
  return isSyncing.value || isFiltering.value
})

const doFilter = debounce(async () => {
  isFiltering.value = true
  filteredResults.value = await filters.filter(all.value)
}, 500)

const handleOpenSelectCustomers = () => {
  restoreFilters(_campaign.value.filters)
  customerIDs.value = _campaign.value.customerIDs || []

  doFilter()

  modal.value.open()
}

const handleSelectAll = () => {
  const visibleIDs = selectedValidResultIDs.value

  if (hasSelectedAllValidResults.value) {
    const visibleIDKeys = new Set(visibleIDs.map(customerIdKey))
    customerIDs.value = customerIDs.value
      .filter((id: any) => !visibleIDKeys.has(customerIdKey(id)))
  } else {
    const selectedIDKeys = new Set(customerIDs.value.map(customerIdKey))
    const missingVisibleIDs = visibleIDs
      .filter((id: any) => !selectedIDKeys.has(customerIdKey(id)))

    customerIDs.value = Array.from(new Set([
      ...customerIDs.value,
      ...missingVisibleIDs,
    ]))
  }
}

const handleToggleCustomer = (id: any, event: Event) => {
  const checked = (event.target as HTMLInputElement).checked

  if (checked) {
    if (!isCustomerSelected(id)) {
      customerIDs.value = [...customerIDs.value, id]
    }
    return
  }

  const key = customerIdKey(id)
  customerIDs.value = customerIDs.value
    .filter((customerID: any) => customerIdKey(customerID) !== key)
}

const handleConfirm = () => {
  const filtersJSON = filters.toJSON()
  const customerIDsValue = customerIDs.value.filter((id: any) => {
    const customer = customerByID.value.get(customerIdKey(id))
    return !customer || isSelectableCustomer(customer)
  })

  _campaign.value.customerIDs = customerIDsValue
  _campaign.value.customerCount = customerIDsValue.length
  _campaign.value.filters = filtersJSON
  emit('update:campaign', _campaign.value)

  modal.value.close()
}

watch(filterValues, () => {
  doFilter()
}, { deep: true })

watch(all, () => {
  if (!filters.isEmpty) {
    doFilter()
  }
})

watch(filteredResults, () => {
  isFiltering.value = false
})

watch(forms, () => {
  filters.updateFormOptions()
})

watch(employees, () => {
  filters.updateEmployeeOptions()
})

watch(services, () => {
  filters.updateServiceOptions()
})

watch(segments, () => {
  filters.updateSegmentOptions()
})

watch(products, () => {
  filters.updateProductOptions()
})

const restoreFilters = (value?: Record<string, any> | null) => {
  filters.clear()
  filters.fromJSON(value || {})
}

onMounted(() => {
  customerStore.sync()
  serviceStore.sync()
  employeeStore.sync()
  formStore.sync()
  segmentStore.sync()
  productStore.sync()

  restoreFilters(props.campaign.filters)
})
</script>
